import React, { useEffect } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@material-ui/core'
import Typography from '../Typography'

/**
 * Describes the Table column item
 * @typedef {Object} TableColumn
 * @property {string} field - The column field. (required)
 * @property {string || React Component} headerName - The displayed column title
 * @property {boolean=} sortable - Controls whether the sorting option is displayed or not
 * @property {function(): object=} renderCell - Allows passing a component into the cell for this column. (required)
 * @example
 * const columns = [
    { field: 'id', headerName: 'Id', sortable: false },
    {
        field: 'tags',
        headerName: 'Tags',
        sortable: false,
        flex: 1,
        renderCell: (params) => (
            <React.Fragment>
                {params.value.map(item => {
                    return <Chip label={item} />
                })}
            </React.Fragment>
        ),
    },
];
 */

export default function TableWrapper({ defaultSort, sortChange, ...overrides }) {
  const [order, setOrder] = React.useState('')
  const [orderBy, setOrderBy] = React.useState('')

  const tableConfig = { ...overrides }
  const columns = tableConfig.columns
  const rows = tableConfig.rows
  const TableRowProps = tableConfig.TableRowProps
  const TableHeaderProps = tableConfig.TableHeaderProps
  const TableCellProps = tableConfig.TableCellProps

  useEffect(() => {
    if (defaultSort.startsWith('-')) {
      setOrder('desc')
      setOrderBy(defaultSort.replace(/^-/, ''))
    } else {
      setOrder('asc')
      setOrderBy(defaultSort)
    }
  }, [defaultSort])

  const handleRequestSort = (event, property) => {
    let sort
    const isAsc = orderBy === property && order === 'asc'
    if (isAsc) {
      sort = `-${property}`
      setOrder('desc')
    } else {
      sort = property
      setOrder('asc')
    }
    setOrderBy(property)
    sortChange(sort)
  }
  return (
    <TableContainer>
      {
        <Table aria-label="table" {...tableConfig}>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column.field} {...TableHeaderProps}>
                  {!column.sortable ? (
                    <Typography align="left" noWrap variant="subtitle2">
                      <strong>{column.headerName}</strong>
                    </Typography>
                  ) : (
                    <TableSortLabel
                      active={orderBy === column.field}
                      direction={orderBy === column.field ? order : 'asc'}
                      onClick={e => handleRequestSort(e, column.field)}
                    >
                      <Typography align="left" color="secondary" noWrap variant="subtitle2">
                        <strong>{column.headerName}</strong>
                      </Typography>
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow hover key={row.id} {...TableRowProps}>
                {columns.map(column => (
                  <TableCell key={column.field} {...TableCellProps}>
                    {column.renderCell(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      }
    </TableContainer>
  )
}
